// Mock data for Stone Temple Partners website - V2

export const firmSnapshot = {
  aum: '$1.2B',
  aua: '$2.8B',
  employeeOwned: '100%',
  employees: 12,
  investmentPros: 6,
  planningPros: 5,
  asOfDate: 'January 1, 2026',
};

export const offices = [
  { name: 'New York', address: '535 Fifth Avenue, Suite 2400', phone: '(917) 837-4617', primary: true },
  { name: 'Philadelphia', address: '1650 Market Street, Suite 3600', phone: '(917) 837-4617', primary: false },
  { name: 'San Francisco', address: '505 Montgomery Street, Suite 1100', phone: '(917) 837-4617', primary: false },
];

// Four pillars — "what earns us the right to do this"
export const pillars = [
  {
    title: 'Family Office DNA',
    desc: "We don't merely study family offices — we run them. Building sophisticated single-family offices from the ground up, we invest our own capital and share every decision our clients face.",
  },
  {
    title: 'Wall Street Investment Depth',
    desc: 'Decades managing institutional capital at major Wall Street firms and buy-side funds. We know how institutions think and where the real edge lives.',
  },
  {
    title: 'Proprietary Sourcing',
    desc: "Relationships that don't exist on any platform. Exclusive channels built through decades of manager, family office, and institutional trust.",
  },
  {
    title: 'Disciplined Risk Management',
    desc: 'Trained in the best quant and fundamental risk frameworks. We take protecting principal as seriously as generating returns.',
  },
];

// Client segments (Who We Serve)
export const clientSegments = [
  { title: 'Founders & Entrepreneurs', desc: 'Concentrated positions, pre- and post-liquidity planning, and diversified portfolios without triggering unnecessary tax events.' },
  { title: 'Multi-Generational Families', desc: 'Governance, education, and stewardship across the family tree — structures that align investment with intent.' },
  { title: 'Family Offices & Principals', desc: "A coordinated advisory layer alongside your existing team — for principals who want institutional-grade portfolio construction without rebuilding what you've already built." },
  { title: 'C-Suite Executives', desc: 'Deferred compensation, restricted stock, and integrated tax planning for leaders with complex, time-constrained compensation.' },
  { title: 'Foundations & Non-Profits', desc: 'Mission-aligned portfolios, spending policies, and manager selection built to endure past any one board or director.' },
  { title: 'Cross-Border Families', desc: 'Consolidated reporting and coordinated advisory across jurisdictions, entities, and currencies.' },
];

// Six Domains of Full Spectrum Advisory
export const sixDomains = [
  { id: '01', title: 'Lifestyle Needs', desc: 'Current and forward-looking spending, real estate, and personal balance sheet.', output: 'Defines the personal expense ratio — the minimum the portfolio must earn before wealth creation begins.' },
  { id: '02', title: 'Governance & Stewardship', desc: 'Family decision frameworks, roles, and generational engagement.', output: "Ensures wealth serves the family's values, not just its balance sheet." },
  { id: '03', title: 'Wealth Transfer', desc: 'Trust structures, intergenerational planning, and beneficiary optimization.', output: 'Aligns estate architecture with investment strategy across generations.' },
  { id: '04', title: 'Liquidity Planning', desc: 'Cash flow modeling, capital commitment pacing, and distribution analysis.', output: "Matches the portfolio's liquidity profile to the family's real obligations." },
  { id: '05', title: 'Legacy Design', desc: 'Philanthropy, impact investing, and long-term family purpose.', output: 'Translates values into a structured giving and impact strategy.' },
  { id: '06', title: 'Investment Strategy', desc: 'Disciplined portfolio management across public and private markets.', output: 'Focuses on purpose-based construction where every dollar earns its place.' },
];

// RIA Consulting benefits
export const riaBenefits = [
  {
    title: 'Look Like the Specialist',
    desc: 'Institutional-grade frameworks — purpose-based investing, philanthropy, next-gen education, concentrated stock — brought into your own client conversations.',
  },
  {
    title: 'Pay for What You Use',
    desc: 'No new hires or in-house specialists. Cost follows the engagement, expanding and contracting with your book.',
  },
  {
    title: 'Deepen Existing Relationships',
    desc: "The complex conversations that build the most loyalty are exactly where STP's frameworks help you win.",
  },
];

export const riaServices = [
  'Wealth Transfer Strategies',
  'Concentrated Asset Solutions',
  'Tax Planning',
  'Philanthropic Strategy',
  'Liquidity Planning',
  'Entity Structuring',
  'Governance & Education',
];

// Investment Solutions - Six-Vehicle Grid
export const vehicleGrid = {
  tiers: ['Daily Liquid', 'Evergreen', 'Drawdown'],
  vehicles: {
    'Daily Liquid': {
      growth: { name: 'Public Equity Solutions', target: '4–6% target tax alpha', description: 'Passive equity exposure with tax loss harvesting and active extension.', tag: 'Full daily access' },
      income: { name: 'Fixed Income Solutions', target: '1.5–2% over SOFR target', description: 'Fixed income mutual funds and ETFs blended to increase yield at lower risk.', tag: 'Full daily access' },
    },
    'Evergreen': {
      growth: { name: 'Liquid Alternatives Fund', target: '8–12% net return target', description: 'Low-net, sector-specific, and structural arbitrage managers with uncorrelated returns.', tag: 'Quarterly liquidity' },
      income: { name: 'Alternative Yield Fund', target: '8–11% cash yield target', description: 'Short-duration income with strong collateral, covenants, and self-liquidating structures.', tag: 'Quarterly liquidity' },
    },
    'Drawdown': {
      growth: { name: 'Growth Opportunities Fund', target: '20%+ net IRR target', description: "STP's best ideas for generating high-multiple returns through mispriced, dislocated, and value-add niche strategies.", tag: '3–7 year horizon' },
      income: { name: 'Income Opportunities Fund', target: '15%+ net IRR target', description: 'STP\'s best ideas over long periods — cash-flow-heavy and high absolute return through tax-efficient, niche strategies.', tag: '3–7 year horizon' },
    },
  },
};

export const fundDetails = [
  {
    name: 'Liquid Alternatives Fund',
    tier: 'Evergreen',
    purpose: 'Growth',
    description: 'Uncorrelated growth with meaningfully less drawdown risk than public equities, through diversified, transparent specialist managers (quantitative systematic, relative value arbitrage, low-net equity).',
    stats: [
      { label: 'Target Net Return', value: '8–12%' },
      { label: 'Target Sharpe', value: '1.5+' },
      { label: 'Target Max Draw', value: '<10%' },
      { label: 'Liquidity', value: 'Quarterly' },
    ],
  },
  {
    name: 'Alternative Yield Fund',
    tier: 'Evergreen',
    purpose: 'Income',
    description: 'Institutional-quality cash income, engineered to avoid the complexity, opacity, and layered fees typical of private credit (short-duration self-liquidating assets, hard-asset collateral, low correlation to credit cycles).',
    stats: [
      { label: 'Target Cash Yield', value: '8–11%' },
      { label: 'Target Net Return', value: '10–13%' },
      { label: 'Duration', value: '18–36 mo' },
      { label: 'Est. Beta', value: '0.3' },
    ],
  },
  {
    name: 'Income Opportunities Fund',
    tier: 'Drawdown',
    purpose: 'Income',
    description: "Best-ideas income vehicle sourcing tax-efficient cash flow across all five alternative asset classes — not private credit alone (day-one uncorrelated cash flow, tax-advantaged structuring, asset-backed protection).",
    stats: [
      { label: 'Target Net IRR', value: '15%+' },
      { label: 'Target Cash Yield', value: '10%+' },
      { label: 'Horizon', value: '3–7 yr' },
      { label: 'Tax Efficiency', value: 'High' },
    ],
  },
  {
    name: 'Growth Opportunities Fund',
    tier: 'Drawdown',
    purpose: 'Growth',
    description: 'Concentrated, best-ideas growth vehicle built for faster paybacks than typical PE/VC vintage funds (11–15 high-conviction positions, compressed J-curve, asymmetric payoff profile).',
    stats: [
      { label: 'Target Net IRR', value: '20%+' },
      { label: 'Target Alpha', value: '5%+' },
      { label: 'Target MOIC', value: '2x+' },
      { label: 'Tax Efficiency', value: 'High' },
    ],
  },
];

export const accessOptions = [
  { title: 'Direct Investments', desc: 'Access to STP-sourced deals alongside our own capital, structured for co-investment.' },
  { title: 'Access Funds', desc: 'Diversified vehicles across purpose-based tiers — the core STP investment offering.' },
  { title: 'Tailored Solutions', desc: 'Public and alternative SMAs and single-asset SPVs designed for concentrated positions and bespoke mandates.' },
];

// Founders (highlighted section at top of Who We Are)
export const founders = [
  {
    slug: 'tim-hall',
    name: 'Tim Hall',
    title: 'Founder & CEO',
    tenure: '25 years',
    photo: 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/4ztfapug_Tim%20Hall%20Headshot.webp',
    summary: 'Trusted advisor to UHNW families, endowments, and institutions across more than two decades; established and led three single-family offices before founding STP.',
    detail: [
      'Since 2019, Tim has been structuring and managing single-family offices — overseeing investment management and running advisory services.',
      'Since 2000, Tim has advised ultra-high-net-worth families, endowments, and institutions.',
      'Twenty-five+ years of investment experience as a portfolio manager, capital allocator, and investment committee member.',
    ],
    email: 'thall@stpartners.co',
    phone: '(917) 837-4617',
  },
  {
    slug: 'alex-herman',
    name: 'Alex Herman',
    title: 'Founder & CIO',
    tenure: '16 years',
    photo: 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/n9cbgjwg_Alex%20Herman%20Headshot%20-%20Copy.webp',
    summary: 'Leads all investment decisions. Former Goldman Sachs macro prop trader; operationalized ideas through every crisis since the GFC.',
    detail: [
      'Since 2019, Alex has invested and built family office portfolios and the family office model portfolio at a boutique investment firm.',
      'Since 2019, Alex has sat on firm and family investment committees.',
      'Four years as a hedge fund portfolio manager in distressed debt and structured products.',
      'Six years at Goldman Sachs as a macro prop and bank credit trader.',
      'An investment manager since 2006.',
    ],
    email: 'aherman@stpartners.co',
    phone: '(917) 555-0181',
  },
  {
    slug: 'david-ward',
    name: 'David Ward',
    title: 'Founder & COO',
    tenure: '25 years',
    photo: 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/qogjmax9_David%20Ward%20Headshot%20-%20Copy.webp',
    summary: 'Architect of the infrastructure powering STP Advisory and Investment Solutions; built systems for four hedge funds and multiple SFOs.',
    detail: [
      'Eight years of experience building out the operational infrastructure, systems, and management of single-family offices at a boutique investment firm.',
      'Fifteen years building and running teams, systems, and raising capital for four different hedge funds.',
      'Ten years at large sell-side firms trading mortgage-backed securities and structured securities.',
      'Thirty years of experience in investment advisory.',
    ],
    email: 'dward@stpartners.co',
    phone: '(203) 253-1823',
  },
];

// Investment Team
export const investmentTeam = [
  {
    slug: 'art-holly',
    name: 'Art Holly',
    title: 'Head of Public Markets',
    photo: 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/zcmtvun9_Art%20Holly%20Headshot%20-%20Copy.webp',
    summary: 'Portfolio and risk management leader with over three decades of experience, creating disciplined, risk-aware investment strategies for UHNW clients.',
    detail: [
      'Head of Portfolio Management and Investment Solutions in the U.S. for Man Group, plc — the world\'s largest listed hedge fund with $167 billion in assets under management.',
      'Significant direct investment experience having run proprietary trading desks at UBS and BofA.',
    ],
  },
  {
    slug: 'jeboah-joerg',
    name: 'Jeboah Joerg',
    title: 'Director of Private Investments',
    photo: 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/6vrl3zo8_Bo%20Joerg%20Headshot%20-%20Copy.webp',
    summary: 'Investment leader with two decades of experience in financial markets, specializing in innovative transaction structuring, risk management, and capital allocation.',
    detail: [
      '16 years\' experience in financial markets and investments.',
      'Co-founder and CIO of HomePace Inc., an originator in the HEI space acquired in 2024. Bo led the effort to scale the company\'s balance sheet through forward flow and structured credit transactions.',
      '8 years\' experience as a Trader / PM at Goldman Sachs specializing in convertible debt and long-dated derivatives.',
    ],
  },
  {
    slug: 'andy-goldberger',
    name: 'Andy Goldberger',
    title: 'Senior Managing Director & Senior PM',
    photo: 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/nsh4y1nt_Screenshot%202026-09-25%20at%2011.07.45%E2%80%AFPM.png',
    summary: 'Investment leader with two decades of buy-side experience, delivering disciplined strategies across hedge funds, family offices, and capital allocation for UHNW clients.',
    detail: [
      '19 years of experience as a buy-side direct investor and capital allocator.',
      '7 years managing single- and multi-family offices and alternative investment platforms.',
      '6 years as a senior member of the hedge fund platform at JPMorgan.',
      '6 years of experience as an analyst and portfolio manager at a multi-strategy hedge fund.',
    ],
  },
  {
    slug: 'alec-armstrong',
    name: 'Alec Armstrong',
    title: 'Associate',
    photo: 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/1qihaf6x_Alec%20Armstrong%20Headshot%20-%20Copy.webp',
    summary: 'Investment professional with three years of experience in private investments, investment diligence, and marketing solutions.',
    detail: [
      '1 year interning at Harken Capital, a private equity placement firm.',
      '3 years assisting the investment team by creating marketing materials and assisting in due diligence.',
    ],
  },
];

// Team
export const team = [
  {
    slug: 'helen-shin',
    name: 'Helen Shin',
    title: 'Chief Legal Officer',
    tenure: '24 Years | Corporate Law & Cross-Border Transactions',
    office: 'New York',
    email: 'hshin@stpartners.co',
    phone: '(917) 555-0116',
    photo: 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/l4rxtm20_Helen%20Shin%20Headshot%20Crop.webp',
    summary: 'Corporate legal leader with over two decades of experience guiding cross-border finance, M&A, and structured transactions for global institutions.',
    detail: [
      '17 years of experience as in-house counsel for a Fortune 500 company responsible for non-equity corporate finance projects, cross-border sourcing and distribution, and international M&A transactions.',
      '7 years of experience structuring and negotiating LBO financings, secured and unsecured credit facilities, private placements and public offerings, and M&A transactions.',
    ],
  },
  {
    slug: 'debbie-staal',
    name: 'Debbie Staal',
    title: 'Director of Operations',
    tenure: '25 Years | Tax, Operations & Family Office Leadership',
    office: 'Philadelphia',
    email: 'dstaal@stpartners.co',
    phone: '(215) 555-0114',
    photo: 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/4p5y5tel_Debbie%20Staal%20Headshot.webp',
    summary: 'Tax and operations leader with 25 years of experience across private equity, family office, and Big Four advisory — building the systems that keep complex families running.',
    detail: [
      '16 years as a tax director for two private equity funds, driving cross-border tax structures and leading tax, finance, and family office functions.',
      '9 years as a tax professional at Deloitte and KPMG managing KKR and Blackstone\'s tax and high-net-worth individual engagements.',
    ],
  },
  {
    slug: 'blake-morrison',
    name: 'Blake Morrison',
    title: 'Senior Wealth Advisor',
    tenure: '20+ Years | FO Advisory & Alternative Investments',
    office: 'San Francisco',
    email: 'bmorrison@stpartners.co',
    phone: '(415) 555-0175',
    photo: 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/bjevx4t0_Blake%20Morrison%20Headshot%203%20-%20Copy.webp',
    summary: 'Wealth advisor with over two decades of family office and hedge fund research experience, leading investment strategy across multi-manager and co-investment platforms.',
    detail: [
      'Over 20 years of family office advisory service and investment experience.',
      'Head of Research for Lanx Management, a multi-manager hedge fund and co-investment platform for family offices.',
    ],
  },
  {
    slug: 'jessica-ji',
    name: 'Jessica Ji',
    title: 'Senior Operations Analyst',
    tenure: '20+ Years | Tax, Fund Accounting & Partnership Operations',
    office: 'New York',
    email: 'jji@stpartners.co',
    phone: '(917) 555-0142',
    photo: 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/xxwbmxv8_Screenshot%202026-09-25%20at%2011.08.51%E2%80%AFPM.png',
    summary: 'Operations professional with over two decades of tax experience, including 16 years managing fund accounting and associated tax at Lindsay Goldberg & Co.',
    detail: [
      'Over 20 years of tax experience.',
      '16 years at Lindsay Goldberg & Co. as a tax manager specializing in fund accounting and tax.',
    ],
  },
];

// How We Think - 5 topics with brief descriptions
export const thinkingTopics = [
  {
    slug: 'full-spectrum-advisory',
    number: '01',
    title: 'Full Spectrum Advisory Process',
    excerpt: 'An integrated approach to wealth management that treats every financial decision as part of a cohesive strategy across six interconnected domains.',
    body: 'Full Spectrum Advisory is STP\'s integrated approach to wealth management. It treats every financial decision as part of a cohesive and coherent strategy for achieving your family\'s goals — organized around lifestyle needs, governance and stewardship, wealth transfer, liquidity planning, legacy design, and investment strategy.',
    tag: 'FRAMEWORK',
    color: 'navy',
  },
  {
    slug: 'purpose-built-portfolios',
    number: '02',
    title: 'Purpose-Built Portfolios',
    excerpt: 'Portfolios organized by what each dollar does — growth or income across a liquidity spectrum — rather than by traditional asset-class buckets.',
    body: 'Purpose-based portfolios shift the question from "what is it?" to "what does it do for the portfolio?" Every investment must earn its place. Growth strategies drive long-term wealth creation; income strategies provide cash flow, inflation protection, and downside cushion. The result: clearer choices and better outcomes.',
    tag: 'INVESTMENTS',
    color: 'beige',
  },
  {
    slug: 'concentrated-assets',
    number: '03',
    title: 'Concentrated Assets Solutions',
    excerpt: 'A multi-faceted plan to reduce concentrated positions over time — pre- and post-monetization — in a cost-effective and tax-efficient manner.',
    body: 'Founder positions rarely fit inside a portfolio, but they define it. STP designs comprehensive diversification plans that use long/short tax overlays, dynastic trusts, private foundations, and staged liquidity — turning a single large asset into a fully constructed family balance sheet.',
    tag: 'CASE STUDY',
    color: 'navy',
  },
  {
    slug: 'wealth-transfer',
    number: '04',
    title: 'Wealth Transfer & Philanthropy',
    excerpt: 'Trust and estate planning, gifting, and philanthropic strategy that align estate architecture with investment strategy across generations.',
    body: 'A well-structured plan on paper is not the same as an executed one. We coordinate estate attorneys, trustees, and family stakeholders to move from vision to execution — accelerated transfer strategies, dynasty trusts, private foundations, and DAF structures aligned to the family\'s long-term intent.',
    tag: 'PLANNING',
    color: 'beige',
  },
  {
    slug: 'next-gen-education',
    number: '05',
    title: 'Next Generation Education',
    excerpt: 'A structured framework for generational engagement — building the skills, confidence, and judgment the next generation will need as stewards.',
    body: 'The most durable family plans invest in people, not just portfolios. STP\'s next-generation program combines governance frameworks, investment education, decision-making rituals, and family committees so heirs step into stewardship prepared — not surprised.',
    tag: 'GOVERNANCE',
    color: 'navy',
  },
];

// Backward-compat exports used by older components (kept but re-mapped)
export const insights = thinkingTopics.map(t => ({
  slug: t.slug,
  category: t.tag.charAt(0) + t.tag.slice(1).toLowerCase(),
  title: t.title,
  excerpt: t.excerpt,
  date: '2026',
  type: 'Read',
  color: t.color,
}));

export const services = [
  { title: 'Financial Planning', desc: 'Comprehensive planning aligned with your goals, tax situation, and multigenerational vision.' },
  { title: 'Wealth Transfer Strategies', desc: 'Trust structures, gifting plans, and estate architecture tailored to your family.' },
  { title: 'Investment Strategy', desc: 'Disciplined portfolio management across public and private markets.' },
  { title: 'Global Reporting', desc: 'A consolidated view of your entire balance sheet, updated as life unfolds.' },
  { title: 'Governance & Education', desc: 'Family decision frameworks, roles, and generational engagement.' },
  { title: 'Philanthropy & Impact', desc: 'Purposeful philanthropy, impact investing, and long-term family legacy.' },
  { title: 'Advisor Network Oversight', desc: 'We coordinate every legal, tax, insurance, and lifestyle partner on your behalf.' },
  { title: 'Liquidity Planning', desc: 'Cash flow modeling and capital commitment pacing that matches your obligations.' },
];

export const process = [
  { step: '01', title: 'Discovery', desc: 'Exploratory conversation around the Six Domains and prioritizing goals. Preliminary wealth assessment.' },
  { step: '02', title: 'Data Collection & Onboarding', desc: 'Full balance sheet assembly, legal documents, entity structure, and system integration.' },
  { step: '03', title: 'Strategy Design', desc: 'Set baseline return and risk tolerance through discovery. Score the stage and progress. Target portfolio construction.' },
  { step: '04', title: 'Presentation & Approval', desc: 'Full Spectrum recommendations reviewed, adjusted, and formalized with the family.' },
  { step: '05', title: 'Execution & Stewardship', desc: 'Portfolio implementation. Ongoing monitoring and active management. The Foundation as the living source of truth.' },
];

export const portfolios = [
  { name: 'Lifestyle Portfolio', purpose: 'Capital preservation + income', horizon: '0–10 years, rolling', priority: 'Liquidity, stability, spending needs', tags: ['Lifestyle needs', 'Liquidity planning'] },
  { name: 'Transfer Portfolio', purpose: 'Long-duration growth + tax efficiency', horizon: '10–30+ years, intergenerational', priority: 'Growth, estate efficiency, education', tags: ['Wealth transfer', 'Governance & Stewardship'] },
  { name: 'Philanthropic Portfolio', purpose: 'Impact + tax-efficient giving', horizon: 'Perpetual or defined mission', priority: 'Alignment with mission and community', tags: ['Legacy design', 'Impact investing'] },
];
